import React, { useContext, useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Avatar,
  AvatarBadge,
  Heading,
  Text,
  VStack,
  Icon,
  Button,
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  Divider,
  Input,
  HStack,
  Spinner,
  useToast,
  Link,
} from "@chakra-ui/react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaGlobe,
} from "react-icons/fa";
import ApiService from "../services/ApiService";
import { resolveProfileImageSrc, compressProfileImage } from "../utils/profileImage";

const formatOfficeAddress = (office) => {
  if (!office) return "-";
  const parts = [
    office.AddressNo,
    office.AddressMoo ? `หมู่ ${office.AddressMoo}` : "",
    office.AddressRoad,
    office.AddressSubdistrict,
    office.AddressDistrict,
    office.Province,
    office.PostalCode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "-";
};

const normalizeWebsiteUrl = (website) => {
  if (!website || typeof website !== "string") return "";
  const trimmed = website.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const InfoRow = ({ icon, label, value }) => (
  <Flex align="center">
    <Icon as={icon} color="gray.500" mr={4} />
    <Box>
      <Text fontSize="sm" color="gray.600">
        {label}
      </Text>
      <Text fontWeight="medium">{value || "-"}</Text>
    </Box>
  </Flex>
);

const InfoLinkRow = ({ icon, label, href, text }) => (
  <Flex align="center">
    <Icon as={icon} color="gray.500" mr={4} />
    <Box>
      <Text fontSize="sm" color="gray.600">
        {label}
      </Text>
      {href ? (
        <Link href={href} isExternal color="blue.600" fontWeight="medium">
          {text || href}
        </Link>
      ) : (
        <Text fontWeight="medium">-</Text>
      )}
    </Box>
  </Flex>
);

const UserProfile = () => {
  const { profile, sessionId } = useContext(AuthContext);
  const navigate = useNavigate();
  const toast = useToast();
  const [employeeData, setEmployeeData] = useState(profile || null);
  const [officeData, setOfficeData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingProfileImage, setPendingProfileImage] = useState(null);
  const [isSavingImage, setIsSavingImage] = useState(false);

  const employeeId = profile?.DLDEmployeeID;

  const fetchEmployeeData = useCallback(async () => {
    if (!employeeId) {
      setEmployeeData(profile || null);
      return;
    }

    setIsLoading(true);
    try {
      const response = await ApiService({
        method: "POST",
        url: "/getDLDEmployeeByID",
        data: { DLDEmployeeID: employeeId },
        sessionId,
      });

      if (response.result === "success" && response.employee) {
        setEmployeeData(response.employee);
        setPendingProfileImage(null);

        const officeId = response.employee.OfficeID || profile?.OfficeID;
        if (officeId) {
          const officeResponse = await ApiService({
            method: "POST",
            url: "/getDLDOfficeByID",
            data: { OfficeID: officeId },
            sessionId,
          });

          if (officeResponse.result === "success" && officeResponse.OfficeList?.[0]) {
            setOfficeData(officeResponse.OfficeList[0]);
          } else {
            setOfficeData(null);
          }
        } else {
          setOfficeData(null);
        }
      }
    } catch (error) {
      console.error("Failed to fetch employee data", error);
    } finally {
      setIsLoading(false);
    }
  }, [employeeId, profile, sessionId]);

  useEffect(() => {
    fetchEmployeeData();
  }, [fetchEmployeeData]);

  const { getInputProps, open } = useDropzone({
    accept: { "image/*": [] },
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop: async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "ไฟล์รูปมีขนาดใหญ่เกินไป",
          description: "กรุณาเลือกรูปที่มีขนาดไม่เกิน 5 MB",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      try {
        const compressedImage = await compressProfileImage(file);
        setPendingProfileImage(compressedImage);
      } catch {
        toast({
          title: "ไม่สามารถประมวลผลรูปภาพได้",
          description: "กรุณาเลือกไฟล์รูปภาพอื่น",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    },
  });

  const handleSaveProfileImage = async () => {
    if (!pendingProfileImage || !employeeId) return;

    setIsSavingImage(true);
    try {
      const response = await ApiService({
        method: "POST",
        url: "/updateEmployeeProfileImage",
        data: {
          DLDEmployeeID: employeeId,
          ProfileImage: pendingProfileImage,
        },
        sessionId,
      });

      if (response.result !== "success") {
        throw new Error(response.error || "ไม่สามารถบันทึกรูปโปรไฟล์ได้");
      }

      toast({
        title: "บันทึกรูปโปรไฟล์สำเร็จ",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      await fetchEmployeeData();
    } catch (error) {
      toast({
        title: error.message || "ไม่สามารถบันทึกรูปโปรไฟล์ได้",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSavingImage(false);
    }
  };

  if (!employeeData && !isLoading) {
    return <Text p={8}>ไม่พบข้อมูลผู้ใช้</Text>;
  }

  if (isLoading && !employeeData) {
    return (
      <Flex justify="center" align="center" height="80vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  const fullName = `${employeeData.FirstName || ""} ${employeeData.LastName || ""}`.trim();
  const profileImageSrc = resolveProfileImageSrc(
    pendingProfileImage || employeeData.ProfileImage
  );
  const officeWebsite = normalizeWebsiteUrl(officeData?.Website);

  return (
    <Box p={{ base: 4, md: 8 }} bg="gray.50" minH="100vh">
      <Flex
        direction={{ base: "column", lg: "row" }}
        gap={8}
        maxW="1200px"
        mx="auto"
      >
        <VStack
          as={Card}
          p={8}
          spacing={4}
          align="center"
          w={{ base: "100%", lg: "350px" }}
        >
          <Box position="relative">
            <Input {...getInputProps()} />
            <Avatar
              size="2xl"
              src={profileImageSrc}
              name={!profileImageSrc ? fullName : undefined}
              icon={<FontAwesomeIcon icon={faUser} size="2x" />}
            >
              <AvatarBadge
                boxSize="1.25em"
                bg={employeeData.Status === "Active" ? "green.500" : "gray.400"}
              />
            </Avatar>
          </Box>

          <Button
            size="sm"
            colorScheme="orange"
            variant="outline"
            onClick={open}
          >
            เปลี่ยนรูปโปรไฟล์
          </Button>

          {pendingProfileImage && (
            <HStack spacing={2}>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={handleSaveProfileImage}
                isLoading={isSavingImage}
                loadingText="กำลังบันทึก..."
              >
                บันทึกรูป
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setPendingProfileImage(null)}
                isDisabled={isSavingImage}
              >
                ยกเลิก
              </Button>
            </HStack>
          )}

          <Text fontSize="xs" color="gray.500" textAlign="center">
            รองรับไฟล์รูปภาพ JPG, PNG
          </Text>

          <VStack spacing={1} align="center">
            <Text fontSize="lg">{fullName || "-"}</Text>
            {employeeData.Position && (
              <Text fontSize="md" color="gray.500">
                ตำแหน่ง : {employeeData.Position}
              </Text>
            )}
            {employeeData.DepartmentName && (
              <Text fontSize="md" color="gray.500">
                สังกัด : {employeeData.DepartmentName}
              </Text>
            )}
          </VStack>

          <VStack spacing={3} w="full">
            <Button
              colorScheme="teal"
              variant="outline"
              w="full"
              onClick={() => navigate("/profile/changepassword")}
            >
              เปลี่ยนแปลงรหัสผ่าน
            </Button>
            <Button
              colorScheme="gray"
              variant="outline"
              w="full"
              onClick={() => navigate(-1)}
            >
              ย้อนกลับ
            </Button>
          </VStack>
        </VStack>

        <Card flex="1">
          <CardHeader>
            <Heading size="md">ข้อมูลส่วนตัว</Heading>
          </CardHeader>
          <Divider color="gray.200" />
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <InfoRow icon={FaEnvelope} label="อีเมล" value={employeeData.Email} />
              <InfoRow icon={FaPhone} label="เบอร์โทรศัพท์" value={employeeData.Phone} />
              <InfoRow
                icon={FaMapMarkerAlt}
                label="ที่อยู่หน่วยงานสังกัด"
                value={formatOfficeAddress(officeData)}
              />
              <InfoLinkRow
                icon={FaGlobe}
                label="เว็บไซต์หน่วยงานสังกัด"
                href={officeWebsite}
                text={officeData?.Website}
              />
            </SimpleGrid>
          </CardBody>
        </Card>
      </Flex>
    </Box>
  );
};

export default UserProfile;
